import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/app/hooks/use-toast";
import DcPage from "@/components/landing/dcv2/DcPage";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, XCircle, Check, X } from "lucide-react";
import { cn } from "@/utils";
import { authApi } from "@/services/endpoints/auth";
import { PasswordRequirements } from "@/components/auth/password-requirements";
import { isPasswordValid } from "@/utils/password-policy";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = isPasswordValid(newPassword) && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !canSubmit) return;
    setLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setSuccess(true);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not reset your password. The link may have expired.";
      toast({ title: "Reset failed", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <DcPage hideFooter>
      {/* The wordmark that sat here came out: the canvas nav above already
          carries it, so the page opened with the same mark twice. The nav is
          also the way back, which is why this needs no link of its own.

          Sized against the nav rather than the viewport -- min-h-screen under a
          72px bar is a screenful plus a bar, so the page scrolled for nothing. */}
      <div className="flex min-h-[calc(100vh-72px)] items-center justify-center p-4">
        <div className="w-full max-w-md">
          {children}
        </div>
      </div>
    </DcPage>
  );

  if (!token) {
    return shell(
      <div className="p-6 sm:p-8 border border-border rounded-lg bg-card text-center space-y-4">
        <XCircle className="mx-auto h-14 w-14 text-destructive" />
        <h1 className="text-2xl font-semibold">Invalid link</h1>
        <p className="text-sm text-muted-foreground">
          This password reset link is invalid or has expired.
        </p>
        <Button className="w-full" onClick={() => navigate("/auth")}>
          Back to sign in
        </Button>
      </div>
    );
  }

  if (success) {
    return shell(
      <div className="p-6 sm:p-8 border border-border rounded-lg bg-card text-center space-y-4">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <h1 className="text-2xl font-semibold">Password reset</h1>
        <p className="text-sm text-muted-foreground">
          Your password has been reset. You can now sign in with your new password.
        </p>
        <Button className="w-full" onClick={() => navigate("/auth")}>
          Go to sign in
        </Button>
      </div>
    );
  }

  return shell(
    <div className="p-6 sm:p-8 border border-border rounded-lg bg-card space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Set a new password</h1>
        <p className="text-sm text-muted-foreground">
          Choose a strong new password to finish resetting your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="newPassword">New password</Label>
          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="newPassword"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Create a strong password"
              className="pl-10 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              aria-label={showNew ? "Hide password" : "Show password"}
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {newPassword.length > 0 && (
            <PasswordRequirements password={newPassword} className="pt-1" />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="confirmPassword"
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter the new password"
              className="pl-10 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
              aria-label={showConfirm ? "Hide password" : "Show password"}
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {confirmPassword.length > 0 && (
            <p
              className={cn(
                "flex items-center gap-1.5 text-[11px] font-medium",
                passwordsMatch ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"
              )}
            >
              {passwordsMatch ? (
                <>
                  <Check className="h-3 w-3 shrink-0" /> Passwords match
                </>
              ) : (
                <>
                  <X className="h-3 w-3 shrink-0" /> Passwords do not match yet
                </>
              )}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
          size="lg"
          disabled={!canSubmit}
          aria-busy={loading}
        >
          {loading ? "Resetting..." : "Reset password"}
        </Button>
      </form>

      <Link
        to="/auth"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sign in
      </Link>
    </div>
  );
}
